// client/src/components/business/FilterPanel.js - CORRECTED VERSION
import React, { useState, useRef, useEffect, memo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import businessService from "../../services/businessService";
import { GEORGIAN_CITIES } from "../../constants/formData";
// REMOVED: Heavy components.css - using optimized components-filters.css instead
import "../../styles/components-filters.css";
import "../../styles/components-overrides.css";
import { getActiveFilterCount } from "../../utils/filterUtils";

const FilterPanel = memo(({ filters, onFilterChange, isMobile = false }) => {
    const { t, i18n } = useTranslation();
    const [openDropdown, setOpenDropdown] = useState(null);
    const dropdownRefs = useRef({});
    // Search state for dropdowns
    const [categorySearch, setCategorySearch] = useState("");
    const [citySearch, setCitySearch] = useState("");

    // Fetch categories
    const { data: categories = [] } = useQuery({
        queryKey: ["categories"],
        queryFn: businessService.getCategories,
    });

    // Use local bilingual cities constant for filter (same as admin panel)
    const cities = GEORGIAN_CITIES;

    // Business type options
    const businessTypes = [
        { value: "individual", label: t('filters.individual') },
        { value: "company", label: t('filters.company') },
    ];

    // Status options (keeping single select)
    const statusOptions = [
        { value: "true", label: t('business.verified') },
        { value: "false", label: t('filters.unverified') },
    ];

    // Handle clicking outside dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                openDropdown &&
                dropdownRefs.current[openDropdown] &&
                !dropdownRefs.current[openDropdown].contains(event.target)
            ) {
                setOpenDropdown(null);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, [openDropdown]);

    // Multi-select handlers
    const handleMultiSelectToggle = (filterType, value) => {
        const currentValues = filters[filterType] || [];
        const newValues = currentValues.includes(value)
            ? currentValues.filter((v) => v !== value)
            : [...currentValues, value];

        onFilterChange({
            ...filters,
            [filterType]: newValues.length > 0 ? newValues : undefined,
        });
    };

    // Single select handler (for status)
    const handleSingleSelect = (filterType, value) => {
        onFilterChange({
            ...filters,
            [filterType]: value || undefined,
        });
        setOpenDropdown(null);
    };

    // Remove single tag
    const removeTag = (filterType, value) => {
        if (Array.isArray(filters[filterType])) {
            const newValues = filters[filterType].filter((v) => v !== value);
            onFilterChange({
                ...filters,
                [filterType]: newValues.length > 0 ? newValues : undefined,
            });
        } else {
            onFilterChange({
                ...filters,
                [filterType]: undefined,
            });
        }
    };

    // Clear all filters
    const clearAllFilters = () => {
        onFilterChange({});
        // Reset search terms when clearing filters
        setCategorySearch("");
        setCitySearch("");
    };

    // Count active filters (shared util)
    const activeFilterCount = getActiveFilterCount(filters);

    // Filter functions for search
    const filterCategories = (categories) => {
        if (!categorySearch.trim()) return categories;
        const searchLower = categorySearch.toLowerCase();
        return categories.filter((cat) => {
            // Search in both English and Georgian names
            const nameEn = cat.name?.en || '';
            const nameKa = cat.name?.ka || '';
            return nameEn.toLowerCase().includes(searchLower) ||
                   nameKa.toLowerCase().includes(searchLower);
        });
    };

    const filterCities = (cities) => {
        if (!citySearch.trim()) return cities;
        const searchLower = citySearch.toLowerCase();
        return cities.filter((city) => {
            // Search in both English and Georgian names
            const nameEn = city.en || '';
            const nameKa = city.ka || '';
            return nameEn.toLowerCase().includes(searchLower) ||
                   nameKa.toLowerCase().includes(searchLower);
        });
    };

    // Enhanced dropdown with search capability
    const renderMultiSelectDropdown = (
        filterType,
        options,
        currentValues = [],
        searchTerm = "",
        setSearchTerm = null
    ) => {
        return (
            <div className="dropdown-content">
                {/* Search input for categories and cities */}
                {(filterType === "categories" || filterType === "cities") && (
                    <div className="dropdown-search">
                        {/* Hidden label for a11y and autofill */}
                        <label htmlFor={`${filterType}-search-input`} className="sr-only">
                            {t('common.search')} {t(`filters.${filterType}`)}
                        </label>
                        <input
                            type="text"
                            id={`${filterType}-search-input`}
                            name={`${filterType}-search`}
                            placeholder={`${t('common.search')} ${t(`filters.${filterType}`).toLowerCase()}...`}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="dropdown-search-input"
                            onClick={(e) => e.stopPropagation()}
                            aria-label={`${t('common.search')} ${t(`filters.${filterType}`).toLowerCase()}`}
                        />
                    </div>
                )}

                {/* Options */}
                {options.filter(option => option != null).map((option) => {
                    // FIXED: Handle different data structures correctly
                    const value =
                        typeof option === "string"
                            ? option
                            : option._id || option.en || option.value; // Use _id for categories, en for cities, value for others
                    const label =
                        typeof option === "string"
                            ? option
                            : option.name
                                ? (option.name[i18n.language] || option.name.en) // Bilingual category name
                                : option.en
                                    ? (option[i18n.language] || option.en) // Bilingual city name
                                    : option.label; // Label for other types
                    const isSelected = currentValues.includes(value);

                    return (
                        <div
                            key={value}
                            className={`dropdown-option ${
                                isSelected ? "selected" : ""
                            }`}
                            onClick={() =>
                                handleMultiSelectToggle(filterType, value)
                            }
                        >
                            <div className="checkbox-wrapper">
                                <input
                                    type="checkbox"
                                    name={`${filterType}[]`}
                                    checked={isSelected}
                                    onChange={() => {}}
                                    className="modern-checkbox"
                                    aria-label={label}
                                />
                                <span className="checkmark"></span>
                            </div>
                            <span className="option-label">{label}</span>
                        </div>
                    );
                })}
            </div>
        );
    };

    // Render single select dropdown
    const renderSingleSelectDropdown = (filterType, options, currentValue) => {
        return (
            <div className="dropdown-content">
                <div
                    className={`dropdown-option ${
                        !currentValue ? "selected" : ""
                    }`}
                    onClick={() => handleSingleSelect(filterType, "")}
                >
                    <div className="radio-wrapper">
                        <input
                            type="radio"
                            name={`${filterType}-option`}
                            checked={!currentValue}
                            onChange={() => {}}
                            className="modern-radio"
                            aria-label={t('filters.allBusinesses')}
                        />
                        <span className="radiomark"></span>
                    </div>
                    <span className="option-label">{t('filters.allBusinesses')}</span>
                </div>
                {options.map((option) => {
                    const value =
                        typeof option === "string" ? option : option.value;
                    const label =
                        typeof option === "string" ? option : option.label;
                    const isSelected = currentValue === value;

                    return (
                        <div
                            key={value}
                            className={`dropdown-option ${
                                isSelected ? "selected" : ""
                            }`}
                            onClick={() =>
                                handleSingleSelect(filterType, value)
                            }
                        >
                            <div className="radio-wrapper">
                                <input
                                    type="radio"
                                    name={`${filterType}-option`}
                                    checked={isSelected}
                                    onChange={() => {}}
                                    className="modern-radio"
                                    aria-label={label}
                                />
                                <span className="radiomark"></span>
                            </div>
                            <span className="option-label">{label}</span>
                        </div>
                    );
                })}
            </div>
        );
    };

    // Render tags
    const renderTags = (filterType, values, getDisplayName = (v) => v) => {
        if (!values || values.length === 0) return null;

        const valuesArray = Array.isArray(values) ? values : [values];

        return (
            <div className="tags-container">
                {valuesArray.map((value) => (
                    <span key={value} className="filter-tag">
                        <span className="tag-text">
                            {getDisplayName(value)}
                        </span>
                        <button
                            className="tag-remove"
                            onClick={() => removeTag(filterType, value)}
                            aria-label={`Remove ${getDisplayName(
                                value
                            )} filter`}
                        >
                            ×
                        </button>
                    </span>
                ))}
            </div>
        );
    };

    // Get display name for business type
    const getBusinessTypeLabel = (value) => {
        const type = businessTypes.find((t) => t.value === value);
        return type ? type.label : value;
    };

    // Get display name for status
    const getStatusLabel = (value) => {
        const status = statusOptions.find((s) => s.value === value);
        return status
            ? status.label
            : value === "true"
            ? t('business.verified')
            : t('filters.unverified');
    };

    // Get display name for category by ID
    const getCategoryLabel = (categoryId) => {
        const category = categories.find((cat) => cat._id === categoryId);
        if (!category) return categoryId;
        return category.name[i18n.language] || category.name.en;
    };

    // Get display name for city
    const getCityLabel = (cityKey) => {
        const city = cities.find((c) => c.en === cityKey);
        if (!city) return cityKey;
        return city[i18n.language] || city.en;
    };

    

    return (
        <div className={`filter-panel ${isMobile ? "mobile" : ""}`}>
            <div className="filter-header">
                <h3>{t('filters.filtersTitle')}</h3>
                {activeFilterCount > 0 && (
                    <span className="filter-count">{activeFilterCount}</span>
                )}
            </div>

            {/* Categories Filter */}
            <div className="filter-group">
                <label>{t('filters.categories')}:</label>
                <div
                    className="dropdown-wrapper"
                    ref={(el) => (dropdownRefs.current.categories = el)}
                >
                    <button
                        className={`dropdown-trigger ${
                            openDropdown === "categories" ? "open" : ""
                        }`}
                        onClick={() =>
                            setOpenDropdown(
                                openDropdown === "categories"
                                    ? null
                                    : "categories"
                            )
                        }
                    >
                        <span>{t('filters.selectCategories')}</span>
                        <span className="dropdown-arrow">▼</span>
                    </button>

                    {openDropdown === "categories" &&
                        renderMultiSelectDropdown(
                            "categories",
                            filterCategories(categories),
                            filters.categories || [],
                            categorySearch,
                            setCategorySearch
                        )}
                </div>

                {renderTags("categories", filters.categories, getCategoryLabel)}
            </div>

            {/* Cities Filter */}
            <div className="filter-group">
                <label>{t('filters.cities')}:</label>
                <div
                    className="dropdown-wrapper"
                    ref={(el) => (dropdownRefs.current.cities = el)}
                >
                    <button
                        className={`dropdown-trigger ${
                            openDropdown === "cities" ? "open" : ""
                        }`}
                        onClick={() =>
                            setOpenDropdown(
                                openDropdown === "cities" ? null : "cities"
                            )
                        }
                    >
                        <span>{t('filters.selectCities')}</span>
                        <span className="dropdown-arrow">▼</span>
                    </button>

                    {openDropdown === "cities" &&
                        renderMultiSelectDropdown(
                            "cities",
                            filterCities(cities),
                            filters.cities || [],
                            citySearch,
                            setCitySearch
                        )}
                </div>

                {renderTags("cities", filters.cities, getCityLabel)}
            </div>

            {/* Business Type Filter */}
            <div className="filter-group">
                <label>{t('filters.businessType')}:</label>
                <div
                    className="dropdown-wrapper"
                    ref={(el) => (dropdownRefs.current.businessTypes = el)}
                >
                    <button
                        className={`dropdown-trigger ${
                            openDropdown === "businessTypes" ? "open" : ""
                        }`}
                        onClick={() =>
                            setOpenDropdown(
                                openDropdown === "businessTypes"
                                    ? null
                                    : "businessTypes"
                            )
                        }
                    >
                        <span>{t('filters.selectType')}</span>
                        <span className="dropdown-arrow">▼</span>
                    </button>

                    {openDropdown === "businessTypes" &&
                        renderMultiSelectDropdown(
                            "businessTypes",
                            businessTypes,
                            filters.businessTypes || []
                        )}
                </div>

                {renderTags(
                    "businessTypes",
                    filters.businessTypes,
                    getBusinessTypeLabel
                )}
            </div>

            {/* Status Filter (Single Select) */}
            <div className="filter-group">
                <label>{t('filters.status')}:</label>
                <div
                    className="dropdown-wrapper"
                    ref={(el) => (dropdownRefs.current.verified = el)}
                >
                    <button
                        className={`dropdown-trigger ${
                            openDropdown === "verified" ? "open" : ""
                        }`}
                        onClick={() =>
                            setOpenDropdown(
                                openDropdown === "verified" ? null : "verified"
                            )
                        }
                    >
                        <span>
                            {filters.verified
                                ? getStatusLabel(filters.verified)
                                : t('filters.allBusinesses')}
                        </span>
                        <span className="dropdown-arrow">▼</span>
                    </button>

                    {openDropdown === "verified" &&
                        renderSingleSelectDropdown(
                            "verified",
                            statusOptions,
                            filters.verified
                        )}
                </div>

                {filters.verified &&
                    renderTags("verified", filters.verified, getStatusLabel)}
            </div>

            {/* Clear All Filters */}
            {activeFilterCount > 0 && (
                <button className="clear-filters-btn" onClick={clearAllFilters}>
                    {t('filters.clearAllFilters')} ({activeFilterCount})
                </button>
            )}
        </div>
    );
});

// Add display name for better debugging
FilterPanel.displayName = 'FilterPanel';

export default FilterPanel;
